import { useState, useEffect, useRef, useCallback } from 'react';
import { services, cleaners, navLinks, navServiceItems } from '@/mocks/homeData';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'services' | 'form' | 'summary' | 'processing' | 'redirect';

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [step, setStep] = useState<Step>('services');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: 'Lagos',
    propertyType: '',
    serviceType: '',
    date: '',
    time: '',
    hasPets: '',
    notes: '',
    promoCode: '',
    referralSource: '',
  });
  const [assignedCleaners, setAssignedCleaners] = useState<string[]>([]);
  const [processingText, setProcessingText] = useState('Confirming your booking...');
  const modalRef = useRef<HTMLDivElement>(null);

  const service = services.find((s) => s.id === selectedService);

  useEffect(() => {
    if (isOpen) {
      setStep('services');
      setSelectedService(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        city: 'Lagos',
        propertyType: '',
        serviceType: '',
        date: '',
        time: '',
        hasPets: '',
        notes: '',
        promoCode: '',
        referralSource: '',
      });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (step === 'processing') {
      const t1 = setTimeout(() => setProcessingText('Assigning your cleaning crew...'), 1500);
      const t2 = setTimeout(() => setProcessingText('Preparing your schedule...'), 3000);
      const t3 = setTimeout(() => {
        setStep('redirect');
      }, 4500);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
  }, [step]);

  useEffect(() => {
    if (step === 'summary' && service) {
      const count = service.cleaners || 1;
      const shuffled = [...cleaners].sort(() => Math.random() - 0.5);
      setAssignedCleaners(shuffled.slice(0, count).map((c) => c.id));
    }
  }, [step, service]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === modalRef.current) onClose();
  }, [onClose]);

  const handleServiceSelect = (id: string) => {
    setSelectedService(id);
    setStep('form');
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('summary');
  };

  const handleBook = () => {
    setStep('processing');
  };

  const handleRedirect = () => {
    const s = service;
    if (!s) return;
    const assigned = cleaners.filter((c) => assignedCleaners.includes(c.id));
    const cleanersText = assigned.map((c) => `${c.name} (${c.role})`).join(', ');
    const message = encodeURIComponent(
      `Hello CleanUp Naija! I would like to book a ${s.title}.\n\n` +
        `Name: ${formData.name}\n` +
        `Phone: ${formData.phone}\n` +
        `Email: ${formData.email}\n` +
        `Address: ${formData.address}, ${formData.city}\n` +
        `Property Type: ${formData.propertyType}\n` +
        `Service Type Needed: ${formData.serviceType}\n` +
        `Preferred Date: ${formData.date}\n` +
        `Preferred Time: ${formData.time}\n` +
        `Pets on Property: ${formData.hasPets}\n` +
        `Assigned Cleaners: ${cleanersText}\n` +
        `Estimated Duration: ${s.duration}\n` +
        `Price: From ${s.priceFrom}\n\n` +
        (formData.notes ? `Notes: ${formData.notes}\n\n` : '') +
        (formData.promoCode ? `Promo Code: ${formData.promoCode}\n` : '') +
        (formData.referralSource ? `Heard about us via: ${formData.referralSource}\n\n` : '\n') +
        `Please confirm my booking. Thank you!`
    );
    window.open(`https://wa.me/2348002000300?text=${message}`, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[1000] bg-primary-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
    >
      <div className="bg-background-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <img
              src="/cleanup_icon.png"
              alt="CleanUp Naija"
              className="w-12 h-12 object-contain"
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = 'none';
                target.parentElement!.querySelector('.booking-logo-fallback')!.classList.remove('hidden');
              }}
            />
            <div className="booking-logo-fallback hidden w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-sparkling-2-line text-lg" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-heading-sm text-foreground-950">
                {step === 'services' && 'Book a Cleanup'}
                {step === 'form' && 'Your Details'}
                {step === 'summary' && 'Booking Summary'}
                {step === 'processing' && 'Processing'}
                {step === 'redirect' && 'Almost Done'}
              </h3>
              <p className="text-body-xs text-foreground-500">
                {step === 'services' && 'Choose a service to get started'}
                {step === 'form' && service?.title}
                {step === 'summary' && 'Review and confirm your booking'}
                {step === 'processing' && 'Please wait a moment'}
                {step === 'redirect' && 'Redirecting to WhatsApp'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg text-foreground-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Step 1: Service Tiles */}
          {step === 'services' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleServiceSelect(s.id)}
                  className="group text-left bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-300 rounded-lg p-4 transition-all duration-200"
                >
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-36 object-cover rounded-md mb-3"
                  />
                  <h4 className="font-heading font-semibold text-body-md text-foreground-950 mb-1">
                    {s.title}
                  </h4>
                  <p className="text-body-sm text-foreground-600 mb-2 line-clamp-2">
                    {s.shortDesc}
                  </p>
                  {/* <p className="text-body-sm font-semibold text-primary-600">
                    From {s.priceFrom}
                  </p> */}
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Form */}
          {step === 'form' && (
            <form onSubmit={handleFormSubmit} className="space-y-4" data-readdy-form="" id="booking-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    required
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                    placeholder="+234 800 000 0000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-body-sm font-medium text-foreground-700 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                  placeholder="you@email.com"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">Address</label>
                  <input
                    type="text"
                    required
                    name="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                    placeholder="123 Street Name"
                  />
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">City</label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                  >
                    <option>Lagos</option>
                    <option>Abuja</option>
                    <option>Port Harcourt</option>
                    <option>Ibadan</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">Property Type</label>
                  <select
                    name="propertyType"
                    required
                    value={formData.propertyType}
                    onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                  >
                    <option value="">Select property type</option>
                    <option>Studio</option>
                    <option>1-2 Bed Flat</option>
                    <option>3-4 Bed Duplex</option>
                    <option>Office Space</option>
                    <option>Vehicle</option>
                    <option>Estate</option>
                  </select>
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">Service Type Needed</label>
                  <select
                    name="serviceType"
                    required
                    value={formData.serviceType}
                    onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                  >
                    <option value="">Select service type</option>
                    <option>One-Time Deep Clean</option>
                    <option>Regular Maintenance</option>
                    <option>Post-Construction</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">Preferred Date</label>
                  <input
                    type="date"
                    required
                    name="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                  />
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">Preferred Time</label>
                  <select
                    name="time"
                    required
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                  >
                    <option value="">Select time</option>
                    <option>Morning (8AM – 12PM)</option>
                    <option>Afternoon (12PM – 4PM)</option>
                    <option>Evening (4PM – 8PM)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-body-sm font-medium text-foreground-700 mb-1">Do you have pets on the property?</label>
                <div className="flex gap-3">
                  {['Yes', 'No'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setFormData({ ...formData, hasPets: option })}
                      className={`px-5 py-2 rounded-md text-body-sm font-medium border transition-colors ${
                        formData.hasPets === option
                          ? 'bg-primary-500 border-primary-500 text-background-50'
                          : 'border-background-300 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-body-sm font-medium text-foreground-700 mb-1">
                  Additional Notes <span className="text-foreground-400">(optional)</span>
                </label>
                <textarea
                  name="notes"
                  maxLength={500}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50 resize-none"
                  rows={3}
                  placeholder="Any special instructions or requests..."
                />
                <p className="text-body-xs text-foreground-400 mt-1">{formData.notes.length}/500</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">
                    Promo / Discount Code <span className="text-foreground-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="promoCode"
                    value={formData.promoCode}
                    onChange={(e) => setFormData({ ...formData, promoCode: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                    placeholder="Enter code"
                  />
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-foreground-700 mb-1">How did you hear about us?</label>
                  <select
                    name="referralSource"
                    value={formData.referralSource}
                    onChange={(e) => setFormData({ ...formData, referralSource: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-body-md bg-background-50"
                  >
                    <option value="">Select an option</option>
                    <option>Instagram</option>
                    <option>Facebook</option>
                    <option>Flyer</option>
                    <option>Referral</option>
                    <option>Search Engine</option>
                  </select>
                </div>
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                style={{ clip: 'rect(0,0,0,0)' }}
              />
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('services')}
                  className="px-5 py-2.5 rounded-md text-body-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors border border-background-300"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 px-5 py-2.5 rounded-md text-body-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
                >
                  Continue to Summary
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Summary */}
          {step === 'summary' && service && (
            <div className="space-y-5">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <p className="text-label-sm text-primary-700 uppercase tracking-wide mb-1">Selected Service</p>
                <h4 className="font-heading font-semibold text-heading-sm text-foreground-950">{service.title}</h4>
                <p className="text-body-sm text-foreground-600 mt-1">{service.shortDesc}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-label-sm text-foreground-500 uppercase tracking-wide">Duration</p>
                  <p className="font-heading font-semibold text-body-md text-foreground-950 mt-1">{service.duration}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-label-sm text-foreground-500 uppercase tracking-wide">Cleaners</p>
                  <p className="font-heading font-semibold text-body-md text-foreground-950 mt-1">{service.cleaners} assigned</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-label-sm text-foreground-500 uppercase tracking-wide">Price</p>
                  <p className="font-heading font-semibold text-body-md text-primary-600 mt-1">From {service.priceFrom}</p>
                </div>
              </div>

              <div>
                <p className="text-label-sm text-foreground-500 uppercase tracking-wide mb-3">Assigned Cleaning Crew</p>
                <div className="flex flex-wrap gap-3">
                  {cleaners
                    .filter((c) => assignedCleaners.includes(c.id))
                    .map((cleaner) => (
                      <div
                        key={cleaner.id}
                        className="flex items-center gap-3 bg-background-100 rounded-lg p-3 flex-1 min-w-[200px]"
                      >
                        <img
                          src={cleaner.image}
                          alt={cleaner.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium text-body-sm text-foreground-950">{cleaner.name}</p>
                          <p className="text-body-xs text-foreground-500">{cleaner.role}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <i className="ri-star-fill text-yellow-500 text-xs" />
                            <span className="text-body-xs text-foreground-600">{cleaner.rating}</span>
                            <span className="text-body-xs text-foreground-400">({cleaner.jobs} jobs)</span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-4 space-y-2">
                <p className="text-label-sm text-foreground-500 uppercase tracking-wide mb-2">Your Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-body-sm">
                  <p className="text-foreground-700"><span className="text-foreground-400">Name:</span> {formData.name}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Phone:</span> {formData.phone}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Email:</span> {formData.email}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Location:</span> {formData.address}, {formData.city}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Property Type:</span> {formData.propertyType}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Service Type:</span> {formData.serviceType}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Date:</span> {formData.date || 'Not set'}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Time:</span> {formData.time || 'Not set'}</p>
                  <p className="text-foreground-700"><span className="text-foreground-400">Pets on Property:</span> {formData.hasPets || 'Not set'}</p>
                  {formData.promoCode && (
                    <p className="text-foreground-700"><span className="text-foreground-400">Promo Code:</span> {formData.promoCode}</p>
                  )}
                  {formData.referralSource && (
                    <p className="text-foreground-700"><span className="text-foreground-400">Heard about us via:</span> {formData.referralSource}</p>
                  )}
                </div>
                {formData.notes && (
                  <p className="text-body-sm text-foreground-600 pt-1 border-t border-background-200 mt-2">
                    <span className="text-foreground-400">Notes:</span> {formData.notes}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep('form')}
                  className="px-5 py-2.5 rounded-md text-body-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors border border-background-300"
                >
                  Edit Details
                </button>
                <button
                  onClick={handleBook}
                  className="flex-1 px-5 py-2.5 rounded-md text-body-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
                >
                  Book Now
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Processing */}
          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <img
                src="https://readdy.ai/api/search-image?query=A%20clean%20minimalist%20logo%20icon%20design%20for%20a%20Nigerian%20cleaning%20service%20brand%20called%20CleanUp%20Naija%2C%20featuring%20a%20stylized%20sparkle%20or%20water%20droplet%20shape%20in%20a%20vibrant%20warm%20orange-green%20gradient%20on%20a%20white%20background%2C%20flat%20design%2C%20no%20text%2C%20simple%20geometric%20logo%20mark%2C%20professional%20corporate%20brand%20identity%2C%20high%20quality%20vector-style%20illustration&width=200&height=200&seq=navbar-logo-v3&orientation=squarish&nocache=true"
                alt="CleanUp Naija"
                className="w-20 h-20 object-contain animate-loader-pulse mb-6"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  target.parentElement!.querySelector('.booking-loader-fallback')!.classList.remove('hidden');
                }}
              />
              <div className="booking-loader-fallback hidden w-20 h-20 flex items-center justify-center rounded-full bg-primary-100 animate-loader-pulse mb-6">
                <i className="ri-sparkling-2-line text-3xl text-primary-600" />
              </div>
              <p className="font-heading font-semibold text-body-md text-foreground-950 mb-2">
                {processingText}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="w-2 h-2 rounded-full bg-primary-500 animate-loader-pulse" />
                <span className="w-2 h-2 rounded-full bg-primary-500 animate-loader-pulse" style={{ animationDelay: '0.2s' }} />
                <span className="w-2 h-2 rounded-full bg-primary-500 animate-loader-pulse" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          )}

          {/* Step 5: Redirect */}
          {step === 'redirect' && (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                <i className="ri-whatsapp-line text-3xl text-green-600" />
              </div>
              <h4 className="font-heading font-semibold text-heading-md text-foreground-950 mb-2">
                Booking Confirmed!
              </h4>
              <p className="text-body-md text-foreground-600 max-w-sm mb-6">
                We will connect you with our team on WhatsApp to finalize your appointment and answer any questions.
              </p>
              <button
                onClick={handleRedirect}
                className="px-8 py-3 rounded-md text-body-md font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <i className="ri-whatsapp-line" />
                Open WhatsApp
              </button>
              <p className="text-body-xs text-foreground-400 mt-4">
                Or call us directly at +234 800 CLEAN UP
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}